import React from 'react';
import { CartItem } from '../types';

interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  items: CartItem[];
  onUpdateQuantity: (id: string, delta: number) => void;
  onRemove: (id: string) => void;
}

const CartDrawer: React.FC<CartDrawerProps> = ({ isOpen, onClose, items, onUpdateQuantity, onRemove }) => {
  const subtotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const shipping = subtotal > 50 ? 0 : 5.99;
  const total = subtotal + shipping;

  return (
    <>
      <div 
        className={`fixed inset-0 bg-[#5C3E43]/20 backdrop-blur-sm z-50 transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
      />
      <aside 
        className={`fixed top-0 right-0 h-full w-full max-w-md bg-white z-50 transition-transform duration-300 ease-out transform ${
          isOpen ? 'translate-x-0 shadow-2xl' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-white">
            <h2 className="text-lg font-black text-[#5C3E43] uppercase tracking-tight">Shopping Bag ({items.length})</h2>
            <button onClick={onClose} className="p-2 hover:bg-slate-50 rounded-full transition-colors border border-slate-200">
              <i className="fas fa-times text-slate-400 text-sm"></i>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-white">
            {items.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center space-y-4">
                <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center border border-slate-100">
                  <i className="fas fa-shopping-bag text-2xl text-slate-200"></i>
                </div>
                <div>
                  <h3 className="font-bold text-slate-700">Empty Bag</h3>
                  <p className="text-xs text-slate-400 max-w-[200px] mx-auto">Looks like you haven't added any professional solutions yet.</p>
                </div>
              </div>
            ) : (
              items.map((item) => (
                <div key={item.id} className="flex space-x-4 border-b border-slate-50 pb-6">
                  <div className="w-16 h-16 bg-slate-100 rounded-lg overflow-hidden shrink-0">
                    <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 flex flex-col">
                    <div className="flex justify-between">
                      <h4 className="font-bold text-slate-900 text-sm leading-tight">{item.name}</h4>
                      <button onClick={() => onRemove(item.id)} className="text-slate-300 hover:text-red-500 transition-colors">
                        <i className="fas fa-trash text-xs"></i>
                      </button>
                    </div>
                    <div className="flex items-center justify-between mt-auto pt-2">
                      <div className="flex items-center bg-slate-50 rounded-md overflow-hidden border border-slate-100">
                        <button 
                          onClick={() => onUpdateQuantity(item.id, -1)}
                          className="px-2 py-1 hover:bg-slate-200 text-slate-500"
                        >
                          <i className="fas fa-minus text-[8px]"></i>
                        </button>
                        <span className="px-3 py-1 text-xs font-bold text-[#F1B1BA]">{item.quantity}</span>
                        <button 
                          onClick={() => onUpdateQuantity(item.id, 1)}
                          className="px-2 py-1 hover:bg-slate-200 text-slate-500"
                        >
                          <i className="fas fa-plus text-[8px]"></i>
                        </button>
                      </div>
                      <span className="font-black text-slate-900 text-sm">£{(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {items.length > 0 && (
            <div className="p-6 bg-white border-t border-slate-200 space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between text-xs font-bold text-slate-500 uppercase tracking-widest">
                  <span>Subtotal</span>
                  <span>£{subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-xs font-bold text-slate-500 uppercase tracking-widest">
                  <span>Shipping</span>
                  <span className="text-green-600">{shipping === 0 ? 'FREE' : `£${shipping.toFixed(2)}`}</span>
                </div>
                <div className="flex justify-between text-xl font-black text-[#5C3E43] pt-4 border-t border-slate-200">
                  <span>Total</span>
                  <span>£{total.toFixed(2)}</span>
                </div>
              </div>
              <button className="w-full py-4 bg-[#F1B1BA] text-[#5C3E43] rounded-xl font-black shadow-lg shadow-pink-900/10 hover:brightness-105 transition-all uppercase tracking-widest text-sm">
                Secure Checkout
              </button>
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

export default CartDrawer;